import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { db } from './firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { getInitials } from './utils';

export default function LikesModal({ isOpen, onClose, wishlistId, wishlistName }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && wishlistId) {
      fetchLikes();
    }
  }, [isOpen, wishlistId]);

  const fetchLikes = async () => {
    setLoading(true);
    try {
      const wishlistRef = doc(db, 'wishlists', wishlistId);
      const wishlistDoc = await getDoc(wishlistRef);

      if (wishlistDoc.exists()) {
        const wishlistData = wishlistDoc.data();
        // 🔧 FIX: Support all likes formats: array (current), nested object, and legacy likedBy
        const likedByIds = Array.isArray(wishlistData.likes) 
          ? wishlistData.likes 
          : (wishlistData.likes?.userIds || wishlistData.likedBy || []);

        if (likedByIds.length === 0) {
          setUsers([]);
          setLoading(false);
          return;
        }

        const userDetails = await Promise.all(
          likedByIds.map(async (uid) => {
            try {
              const userDetailDoc = await getDoc(doc(db, 'users', uid));
              if (userDetailDoc.exists()) {
                return {
                  id: uid,
                  ...userDetailDoc.data()
                };
              }
              return null;
            } catch (error) {
              console.error(`Error fetching user ${uid}:`, error);
              return null;
            }
          })
        );

        setUsers(userDetails.filter(user => user !== null));
      }
    } catch (error) {
      console.error('Error fetching likes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUserClick = (clickedUserId) => {
    onClose();
    navigate(`/utente/${clickedUserId}`, {
      state: {
        fromPage: window.location.pathname,
        scrollY: window.scrollY
      }
    });
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>
            {t('likes.title')} - {wishlistName}
          </h3>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={styles.content}>
          {loading ? (
            <div style={styles.loading}>{t('common.loading')}</div>
          ) : users.length === 0 ? (
            <div style={styles.empty}>
              {t('likes.noLikes')}
            </div>
          ) : (
            <div style={styles.userList}>
              {users.map((user) => (
                <div 
                  key={user.id} 
                  style={styles.userItem}
                  onClick={() => handleUserClick(user.id)}
                >
                  <div style={styles.userInfo}>
                    {user.avatar ? (
                      <img src={user.avatar} alt={user.displayName} style={styles.avatar} />
                    ) : (
                      <div style={styles.avatarFallback}>
                        {getInitials(user.displayName)}
                      </div>
                    )}
                    <span style={styles.userName}>{user.displayName || t('common.user')}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    width: '90%',
    maxWidth: '450px',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #eee',
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    margin: 0,
    color: '#000',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#666',
    padding: '0',
    width: '30px',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    padding: '20px',
    overflowY: 'auto',
    flex: 1,
  },
  loading: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#666',
  },
  empty: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#999',
  },
  userList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  userItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 8px',
    backgroundColor: '#f5f5f5',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  avatar: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    marginRight: '8px',
  },
  avatarFallback: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    backgroundColor: '#000',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '600',
    fontSize: '14px',
    marginRight: '8px',
  },
  userName: {
    fontSize: '14px',
    fontWeight: '500',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
  },
};
